<template>
    <transition name="fade">
        <div
            v-if="isVisible && currentPopup && Object.keys(currentPopup).length"
            class="ecom-modal"
            :class="{ 'ecom-modal--farmacie': isFarmacie }"
        >
            <div v-click-outside="closePopup" class="ecom-modal__content">
                <button class="ecom-modal__close" @click="closePopup">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <path
                            d="M1.6668 21.513L0.487305 20.3335L9.82064 11.0001L0.487305 1.6668L1.6668 0.487305L11.0001 9.82064L20.3335 0.487305L21.513 1.6668L12.1796 11.0001L21.513 20.3335L20.3335 21.513L11.0001 12.1796L1.6668 21.513Z"
                        />
                    </svg>
                </button>
                <img class="ecom-modal__image" :src="imagePath(currentPopup?.image)" :alt="currentPopup?.name" />
                <div class="ecom-modal__body">
                    <h2 class="ecom-modal__title">{{ currentPopup?.name }}</h2>
                    <p class="ecom-modal__description">{{ currentPopup?.description }}</p>
                    <nuxt-link :to="localePath({ path: currentPopup?.link })" class="ecom-modal__link" @click.native="closePopup">
                        {{ currentPopup?.button_text }}
                    </nuxt-link>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import vClickOutside from 'v-click-outside';
import { mapGetters, mapMutations } from 'vuex';
import imagePath from '@/mixins/imagePath';

export default {
    name: 'EcomMyModal',
    mixins: [imagePath],
    props: {
        popupDataFromProduct: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            isVisible: false,
            currentPopupIndex: 0
        };
    },
    directives: {
        clickOutside: vClickOutside.directive
    },
    computed: {
        ...mapGetters({
            popupData: 'global/getPopup',
            popupIsShowed: 'global/getGlobalPopupIsShowed',
            isFarmacie: 'global/getIsFarmacie'
        }),
        allPopupData() {
            const hasProductPopup = Object.keys(this.popupDataFromProduct || []).length > 0;
            if (hasProductPopup && !this.popupIsShowed) {
                return [...this.popupData, this.popupDataFromProduct];
            } else if (hasProductPopup) {
                return [this.popupDataFromProduct];
            } else {
                return this.popupData;
            }
        },
        currentPopup() {
            if (Array.isArray(this.allPopupData)) {
                return this.allPopupData[this.currentPopupIndex];
            } else if (typeof this.allPopupData === 'object') {
                return this.allPopupData;
            }
            return null;
        }
    },
    mounted() {
        let popupClosed = null;
        if (this.$cookies) {
            popupClosed = this.$cookies.get('popupClosed');
        }

        if (!popupClosed) {
            if (this.popupIsShowed) {
                this.startProductPopup();
            } else {
                this.startPopups();
            }
        }
    },
    methods: {
        ...mapMutations({
            SET_GLOBAL_POPUP_IS_SHOWED: 'global/SET_GLOBAL_POPUP_IS_SHOWED'
        }),
        startPopups() {
            if (this.allPopupData.length > 0) {
                setTimeout(() => {
                    this.showNextPopup();
                }, this.currentPopup.delay_time * 1000);
            }
        },
        startProductPopup() {
            if (this.currentPopup) {
                setTimeout(() => {
                    this.isVisible = true;
                    setTimeout(() => {
                        this.isVisible = false;
                    }, this.currentPopup.active_time * 1000);
                }, this.currentPopup.delay_time * 1000);
            }
        },
        showNextPopup() {
            if (this.currentPopupIndex >= this.allPopupData.length) return;

            this.isVisible = true;
            setTimeout(() => {
                this.isVisible = false;
                setTimeout(() => {
                    this.currentPopupIndex++;
                    if (this.currentPopupIndex < this.allPopupData.length) {
                        this.showNextPopup();
                    } else {
                        this.SET_GLOBAL_POPUP_IS_SHOWED(true);
                    }
                }, this.currentPopup.delay_time * 1000);
            }, this.currentPopup.active_time * 1000);
        },
        closePopup() {
            this.isVisible = false;
            if (this.$cookies) {
                this.$cookies.set('popupClosed', true);
            }
        }
    },
    watch: {
        isVisible(val) {
            document.body.classList.toggle('popup-open', val);
        }
    }
};
</script>

<style scoped lang="scss">
.ecom-modal {
    $root: &;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(25, 25, 25, 0.4);
    z-index: 100;
    padding: 10px 20px;

    &--farmacie {
        #{$root} {
            &__content {
                border-radius: 0;
            }
            &__link {
                border-radius: 0;
            }
        }
    }

    &__content {
        display: grid;
        grid-template-columns: minmax(200px, 600px) minmax(500px, 1fr);
        position: relative;
        width: 100%;
        max-width: 1360px;
        background-color: #fff;
        height: 600px;
        overflow: auto;
        border-radius: 25px;
    }

    &__image {
        display: block;
        width: 100%;
        height: 100%;
        max-height: 600px;
        object-fit: cover;
    }

    &__title {
        color: var(--text-body-strong);
        font-size: 48px;
        font-weight: 500;
        line-height: 1.2;
        margin-bottom: 18px;
        text-align: center;
    }

    &__description {
        width: 100%;
        max-width: 440px;
        font-size: 26px;
        line-height: 1.2;
        color: var(--text-body-strong);
        text-align: center;
        margin-bottom: 33px;
    }

    &__link {
        padding: 16px 32px;
        border-radius: 16px;
        background-color: var(--bg-primary);
        font-size: 18px;
        color: #fff;
        transition: 0.3s;

        &:hover {
            background-color: var(--primary-primary-hover);
        }
    }

    &__close {
        position: absolute;
        right: 24px;
        top: 24px;
        padding: 10px;

        svg {
            fill: #181615;
        }
    }

    &__body {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        min-height: 100%;
        width: 100%;
        padding: 0 64px;
    }

    @media (max-width: 1024px) {
        &__content {
            grid-template-columns: 1fr 1fr;
            height: auto;
            max-height: 100%;
        }

        &__title {
            font-size: 36px;
        }

        &__description {
            font-size: 18px;
            margin-bottom: 24px;
        }
    }

    @media (max-width: 767px) {
        &__content {
            grid-template-columns: 1fr;
        }

        &__image {
            max-height: 296px;
        }

        &__body {
            padding: 32px;
        }
    }
}
</style>
